import {
    useState,
    useEffect,
    useMemo,
    useRef,
    ChangeEventHandler,
    ChangeEvent,
    MouseEventHandler,
} from "react";
import { Moment } from "moment";
import { SelectProps, SelectChangeEvent } from "@mui/material/Select";
import { TabsProps } from "@mui/material/Tabs";
import { modes } from "@core/enums/modes";
import { IAlarmState, ISettings } from "@core/interfaces/ISettings";
import { MomentChangeHandler } from "@core/interfaces/moment-change-handler";
import { mode } from "@core/interfaces/mode";
import { IRawSettings } from "@core/interfaces/IRawSettings";
import { ICloseEvent } from "@core/interfaces/ICloseEvent";
import { BindedHandler } from "@core/interfaces/binded-handler";
import { useStore } from "@ui/hooks/useStore";
import useWindowSize from "@ui/hooks/useWindowSize";
import { getDate, encodeDate } from "@utils/date";
import parseNumber from "@utils/parseNumber";
import parseNumberNullable from "@utils/parseNumberNullable";
import { MomentErrorHandler } from "@core/interfaces/moment-error-handler";
import Settings from "@mui/icons-material/Settings";
import { DeviceTypes } from "@core/enums/device-types";
import { SensorMode } from "./sensor-modes.enum";

enum baseSettings {
    operationMode = "operationMode",
    motionSensor = "motionSensor",
    motionAngleSensor = "motionAngleSensor", // only for MX600LVRU
    lightSensor = "lightSensor",
    dataTransmission = "dataTransmission",
    timezone = "timezone",
}

enum advancedSettins {
    internetSettings = "internetSettings",
    timeSync = "timeSync",
    advancedGnssReceiverSetup = "advancedGnssReceiverSetup",
    updateServer = "updateServer",
    ledIndication = "ledIndication",
}

type baseSetting = `${baseSettings}`;
type advancedSettin = `${advancedSettins}`;
export type tab = baseSetting | advancedSettin;

interface IValidatedInput<T> {
    value: T;
    error: boolean;
}
export interface IDateInput extends IValidatedInput<Moment> {}
export interface INumberInput extends IValidatedInput<number> {}

interface IDefSettings
    extends Omit<
        ISettings,
        | "applied"
        | "ts"
        | "deviceTimezone"
        | "ntpPort"
        | "ftpPort"
        | "gnssMinSearchTime"
        | "gnssMaxSearchTime"
        | "gnssRectificationTime"
        | "mainSim"
        | "coordTxPeriod"
        | "periodicActiveTime"
        | "periodicLocationPeriod"
        | "alarmState"
        | "alarmAction"
        | "accelerometerAmp"
        | "periodicSleepTime"
        | "timeSyncSatsState"
        | "timeSyncGsmState"
        | "gnssPowersave"
        | "serverPort"
        | "motionSensorMode"
        | "timeSyncNtpState"
        | "smsState"
        | "smsFormat"
        | "ignoreTime"
        | "preliminaryAlarmSend"
        | "wakeUpTime"
        | "wakeUpActiveTime"
        | "periodStart"
        | "periodEnd"
        | "ledState"
        | "lsit" // lightIgnoreTime
        | "lsps" // lightPeriodStart
        | "lspe" // lightPeriodEnd
        | "tsit"
        | "tsps"
        | "tspe"
    > {
    ledState: boolean;
    ftpPort: number | null;
    gnssPowersave: boolean;
    gnssMinSearchTime: number;
    gnssMaxSearchTime: number;
    gnssRectificationTime: number;
    timeSyncSatsState: boolean;
    timeSyncGsmState: boolean;
    timeSyncNtpState: boolean;
    ntpPort: number | null;
    serverPort: {
        server1: number | null;
        server2: number | null;
        server3: number | null;
    };
    mainSim: number;
    smsState: boolean;
    smsFormat: number;
    ignoreTime: IDateInput;
    isNotIgnore: boolean;
    preliminaryAlarmSend: boolean;
    accelerometerAmp: number | null;
    periodStart: IDateInput;
    periodEnd: IDateInput;
    coordTxPeriod: number | null;
    periodicActiveTime: number | null;
    periodicSleepDays: number | null;
    periodicSleepHours: number | null;
    periodicSleepMinutes: number | null;
    periodicLocationPeriod: number | null;
    motionSensorMode: number;
    alarmState: IAlarmState<boolean>;
    wakeUpTime: IAlarmState<IDateInput>;
    wakeUpActiveTime: IAlarmState<INumberInput>;
    alarmAction: IAlarmState<number>;
    deviceTimezone: number;
    alarmError: boolean;

    lsit: IDateInput; // lsit
    lsps: IDateInput;
    lspe: IDateInput;
    lightIsNotIgnore: boolean;

    tsit: IDateInput;
    tsps: IDateInput;
    tspe: IDateInput;
    tsaht: number, // angleHoldingTime 1 2 3 5 10 сек
    angleIsNotIgnore: boolean;
}

interface IInitSettings extends IDefSettings {
    periodicSleepError: boolean;
    periodStartAndEndError: boolean;
    lightPeriodStartAndEndError: boolean;
    anglePeriodStartAndEndError: boolean;
    coordTxPeriodError: boolean;
    periodicActiveTimeError: boolean;
    periodicSleepTimeError: boolean;
    periodicSleepDaysError: boolean;
    periodicSleepHoursError: boolean;
    periodicSleepMinutesError: boolean;
    periodicLocationPeriodError: boolean;
    gnssMinSearchTimeError: boolean;
    gnssMaxSearchTimeError: boolean;
}

interface IState extends IInitSettings {
    tab: tab;
    settingsShareVisible: boolean;
    savedAlertVisible: boolean;
    desktopMode: boolean;
    advancedMode: boolean;
    closeALert: boolean;
    isSettingsChanged: boolean;
    motionSensorIgnoreTimeError: boolean;
    lightSensorIgnoreTimeError: boolean;
    angleSensorIgnoreTimeError: boolean;

    sensorMode: SensorMode;
}

const getSensorModeState = (deviceType: string, motionSensorMode: number, angleSensorState: number): SensorMode =>{
    const isMotionSensorOn = motionSensorMode !== 0;
    const isAngleSensorOn = angleSensorState !== 0;

    if(!(deviceType.startsWith("MX500") || deviceType.startsWith("MX510") || deviceType.startsWith("MX600"))){
        return SensorMode.Motion;
    }

    if(!isAngleSensorOn && isMotionSensorOn){
        return SensorMode.Motion;
    }else if(isAngleSensorOn && !isMotionSensorOn){
        return SensorMode.Angle;
    }
    
    return SensorMode.Motion;
}

const useDeviceSettings = (onClose: ICloseEvent["onClose"]) => {
    const { devices, app, lang, user } = useStore();
    const windowSize = useWindowSize();
    const xsBreakpoint = 1000;
    const defDate = getDate(0);

    const { timezone } = user.settings;
    const { deviceName, deviceType } = useMemo(() => {
        const [currentDevice] = devices.deviceList.filter(
            (d) => d.imei === devices.settingDeviceImei
        );
        return {
            deviceName: currentDevice?.name,
            deviceType: currentDevice?.deviceType,
        };
    }, [devices.settingDeviceImei]);
    const { settings: req_settings, mode } = devices.selectedDeviceSettings;
    const settings = req_settings ?? {};
    const {
        coordTxPeriod,
        periodicActiveTime,
        periodicSleepTime,
        periodicLocationPeriod,
        alarmState,
        wakeUpTime,
        wakeUpActiveTime,
        alarmAction,
        motionSensorMode,
        periodStart,
        periodEnd,
        accelerometerAmp,
        ignoreTime,
        preliminaryAlarmSend,
        smsState,
        smsFormat,
        phoneNumSim1,
        phoneNumSim2,
        serverState,
        serverProtocol,
        serverAddress,
        serverPort,
        serverPassword,
        mainSim,
        simApn,
        simLogin,
        simPassword,
        dnsAddress,
        timeSyncSatsState,
        timeSyncGsmState,
        timeSyncNtpState,
        ntpAddress,
        ntpPort,
        gnssPowersave,
        gnssMinSearchTime,
        gnssMaxSearchTime,
        gnssRectificationTime,
        ftpAddress,
        ftpPort,
        ftpLogin,
        ftpPassword,
        ftpDirectory,
        ftpFilename,
        ledState,
        deviceTimezone,

        lsm,
        lssm,
        lsps,
        lspe,
        lsit,

        tsm,
        tsps,
        tspe,
        tsa,
        tsit,
        tsaht,
    } = settings;
    const values = {
        deviceName,
        deviceType,
        preliminaryAlarmSendSupported:
            preliminaryAlarmSend !== undefined,
        timeSyncSatsSupported: timeSyncSatsState !== undefined,
        timeSyncGsmSupported: timeSyncGsmState !== undefined,
        timeSyncNtpSupported: timeSyncNtpState !== undefined,
    };
    const defSettings: IDefSettings = {
        mode: "tracker",
        ledState: false,
        ftpAddress: "",
        ftpPort: null,
        ftpLogin: "",
        ftpPassword: "",
        ftpDirectory: "",
        ftpFilename: "",
        gnssPowersave: false,
        gnssMinSearchTime: 60,
        gnssMaxSearchTime: 5,
        gnssRectificationTime: 0,
        timeSyncSatsState: true,
        timeSyncGsmState: true,
        timeSyncNtpState: true,
        ntpAddress: "",
        ntpPort: null,
        serverState: {
            server1: false,
            server2: false,
            server3: false,
        },
        serverProtocol: {
            server1: "Wialon IPS 2.0",
            server2: "Wialon IPS 2.0",
            server3: "Wialon IPS 2.0",
        },
        serverAddress: {
            server1: "",
            server2: "",
            server3: "",
        },
        serverPort: {
            server1: null,
            server2: null,
            server3: null,
        },
        serverPassword: {
            server1: "",
            server2: "",
            server3: "",
        },
        simApn: {
            sim1: "",
            sim2: "",
        },
        simLogin: {
            sim1: "",
            sim2: "",
        },
        simPassword: {
            sim1: "",
            sim2: "",
        },
        dnsAddress: {
            dns1: "",
            dns2: "",
        },
        mainSim: 1,
        smsState: false,
        smsFormat: 1,
        phoneNumSim1: "",
        phoneNumSim2: "",
        ignoreTime: { value: defDate, error: false },
        isNotIgnore: true,
        preliminaryAlarmSend: false,
        accelerometerAmp: 10,
        periodStart: { value: defDate, error: false },
        periodEnd: { value: defDate, error: false },
        coordTxPeriod: 0,
        periodicActiveTime: 3,
        periodicSleepDays: 0,
        periodicSleepHours: 0,
        periodicSleepMinutes: 1,
        periodicLocationPeriod: 1,
        motionSensorMode: 0,
        alarmState: {
            alarm1: false,
            alarm2: false,
            alarm3: false,
            alarm4: false,
            alarm5: false,
            alarm6: false,
        },
        alarmError: false,
        wakeUpTime: {
            alarm1: { value: defDate, error: false },
            alarm2: { value: defDate, error: false },
            alarm3: { value: defDate, error: false },
            alarm4: { value: defDate, error: false },
            alarm5: { value: defDate, error: false },
            alarm6: { value: defDate, error: false },
        },
        wakeUpActiveTime: {
            alarm1: { value: 3, error: false },
            alarm2: { value: 3, error: false },
            alarm3: { value: 3, error: false },
            alarm4: { value: 3, error: false },
            alarm5: { value: 3, error: false },
            alarm6: { value: 3, error: false },
        },
        alarmAction: {
            alarm1: 1,
            alarm2: 1,
            alarm3: 1,
            alarm4: 1,
            alarm5: 1,
            alarm6: 1,
        },
        deviceTimezone: 0,

        lsm: 0,
        lssm: 0,
        lsps: { value: defDate, error: false },
        lspe: { value: defDate, error: false },
        lsit: { value: defDate, error: false },
        lightIsNotIgnore: true,

        tsm: 0,
        tsps: { value: defDate, error: false },
        tspe: { value: defDate, error: false },
        tsa: 30,
        tsit: { value: defDate, error: false },
        tsaht: 1, // angleHoldingTime 1 2 3 5 10 сек
        angleIsNotIgnore: true,
    };
    const _periodicSleepTime = parseNumber(periodicSleepTime, 1);
    const initSettings: IInitSettings = {
        mode: mode ?? "tracker",
        ledState: ledState === 1 || ledState === "1",
        ftpAddress: ftpAddress ?? "",
        ftpPort: parseNumberNullable(ftpPort),
        ftpLogin: ftpLogin ?? "",
        ftpPassword: ftpPassword ?? "",
        ftpDirectory: ftpDirectory ?? "",
        ftpFilename: ftpFilename ?? "",
        gnssPowersave: gnssPowersave === 1 || gnssPowersave === "1",
        gnssMinSearchTime: parseNumber(gnssMinSearchTime, 60),
        gnssMinSearchTimeError: false,
        gnssMaxSearchTime: parseNumber(gnssMaxSearchTime, 300) / 60,
        gnssMaxSearchTimeError: false,
        gnssRectificationTime: parseNumber(gnssRectificationTime, 0),
        timeSyncSatsState:
            timeSyncSatsState !== undefined
                ? !(timeSyncSatsState === 0 || timeSyncSatsState === "0")
                : true,
        timeSyncGsmState:
            timeSyncGsmState !== undefined
                ? !(timeSyncGsmState === 0 || timeSyncGsmState === "0")
                : true,
        timeSyncNtpState:
            timeSyncNtpState !== undefined
                ? !(timeSyncNtpState === 0 || timeSyncNtpState === "0")
                : true,
        ntpAddress: ntpAddress ?? "",
        ntpPort: parseNumberNullable(ntpPort),
        serverState: {
            server1: serverState?.server1 ?? false,
            server2: serverState?.server2 ?? false,
            server3: serverState?.server3 ?? false,
        },
        serverProtocol: {
            server1: serverProtocol?.server1 ?? "Wialon IPS 2.0",
            server2: serverProtocol?.server2 ?? "Wialon IPS 2.0",
            server3: serverProtocol?.server3 ?? "Wialon IPS 2.0",
        },
        serverAddress: {
            server1: serverAddress?.server1 ?? "",
            server2: serverAddress?.server2 ?? "",
            server3: serverAddress?.server3 ?? "",
        },
        serverPort: {
            server1: parseNumberNullable(serverPort?.server1),
            server2: parseNumberNullable(serverPort?.server2),
            server3: parseNumberNullable(serverPort?.server3),
        },
        serverPassword: {
            server1: serverPassword?.server1 ?? "",
            server2: serverPassword?.server2 ?? "",
            server3: serverPassword?.server3 ?? "",
        },
        simApn: {
            sim1: simApn?.sim1 ?? "",
            sim2: simApn?.sim2 ?? "",
        },
        simLogin: {
            sim1: simLogin?.sim1 ?? "",
            sim2: simLogin?.sim2 ?? "",
        },
        simPassword: {
            sim1: simPassword?.sim1 ?? "",
            sim2: simPassword?.sim2 ?? "",
        },
        dnsAddress: {
            dns1: dnsAddress?.dns1 ?? "",
            dns2: dnsAddress?.dns2 ?? "",
        },
        mainSim: parseNumber(mainSim, 1),
        smsState: smsState === 1 || smsState === "1",
        smsFormat: parseNumber(smsFormat, 0),
        phoneNumSim1: phoneNumSim1 ?? "",
        phoneNumSim2: phoneNumSim2 ?? "",
        ignoreTime: { value: getDate(ignoreTime), error: false },
        isNotIgnore: ignoreTime === 0,
        preliminaryAlarmSend:
            preliminaryAlarmSend === 1 || preliminaryAlarmSend === "1",
        accelerometerAmp: parseNumber(accelerometerAmp, 10),
        periodStart: { value: getDate(periodStart), error: false },
        periodEnd: { value: getDate(periodEnd), error: false },
        periodStartAndEndError: false,
        lightPeriodStartAndEndError: false,
        anglePeriodStartAndEndError: false,
        coordTxPeriod: parseNumber(coordTxPeriod, 0),
        coordTxPeriodError: false,
        periodicActiveTime: parseNumber(periodicActiveTime, 3),
        periodicActiveTimeError: false,
        periodicSleepTimeError: false,
        periodicSleepDays: Math.floor(_periodicSleepTime / 60 / 24),
        periodicSleepHours: Math.floor(
            (_periodicSleepTime -
                Math.floor(_periodicSleepTime / 60 / 24) * 60 * 24) /
                60
        ),
        periodicSleepMinutes:
            _periodicSleepTime -
            Math.floor(_periodicSleepTime / 60 / 24) * 60 * 24 -
            Math.floor(
                (_periodicSleepTime -
                    Math.floor(_periodicSleepTime / 60 / 24) * 60 * 24) /
                    60
            ) *
                60,
        periodicSleepError: false,
        periodicSleepDaysError: false,
        periodicSleepHoursError: false,
        periodicSleepMinutesError: false,
        periodicLocationPeriod: parseNumber(periodicLocationPeriod, 1),
        periodicLocationPeriodError: false,
        motionSensorMode: parseNumber(motionSensorMode, 0),
        alarmState: {
            alarm1: Boolean(alarmState?.alarm1),
            alarm2: Boolean(alarmState?.alarm2),
            alarm3: Boolean(alarmState?.alarm3),
            alarm4: Boolean(alarmState?.alarm4),
            alarm5: Boolean(alarmState?.alarm5),
            alarm6: Boolean(alarmState?.alarm6),
        },
        alarmError: false,
        wakeUpTime: {
            alarm1: {
                value: getDate(wakeUpTime?.alarm1),
                error: false,
            },
            alarm2: {
                value: getDate(wakeUpTime?.alarm2),
                error: false,
            },
            alarm3: {
                value: getDate(wakeUpTime?.alarm3),
                error: false,
            },
            alarm4: {
                value: getDate(wakeUpTime?.alarm4),
                error: false,
            },
            alarm5: {
                value: getDate(wakeUpTime?.alarm5),
                error: false,
            },
            alarm6: {
                value: getDate(wakeUpTime?.alarm6),
                error: false,
            },
        },
        wakeUpActiveTime: {
            alarm1: {
                value: parseNumber(wakeUpActiveTime?.alarm1, 3),
                error: false,
            },
            alarm2: {
                value: parseNumber(wakeUpActiveTime?.alarm2, 3),
                error: false,
            },
            alarm3: {
                value: parseNumber(wakeUpActiveTime?.alarm3, 3),
                error: false,
            },
            alarm4: {
                value: parseNumber(wakeUpActiveTime?.alarm4, 3),
                error: false,
            },
            alarm5: {
                value: parseNumber(wakeUpActiveTime?.alarm5, 3),
                error: false,
            },
            alarm6: {
                value: parseNumber(wakeUpActiveTime?.alarm6, 3),
                error: false,
            },
        },
        alarmAction: {
            alarm1: parseNumber(alarmAction?.alarm1, 0),
            alarm2: parseNumber(alarmAction?.alarm2, 0),
            alarm3: parseNumber(alarmAction?.alarm3, 0),
            alarm4: parseNumber(alarmAction?.alarm4, 0),
            alarm5: parseNumber(alarmAction?.alarm5, 0),
            alarm6: parseNumber(alarmAction?.alarm6, 0),
        },
        deviceTimezone: parseNumber(deviceTimezone, 0),

        lsm: parseNumber(lsm, 0) as 0 | 1 | 2,
        lssm: parseNumber(lssm, 0) as 0 | 1,
        lsps: { value: getDate(lsps), error: false },
        lspe: { value: getDate(lspe), error: false },
        lsit: { value: getDate(lsit), error: false },
        lightIsNotIgnore: lsit === 0,

        tsm: parseNumber(tsm, 0) as 0 | 1 | 2,
        tsps: { value: getDate(tsps), error: false },
        tspe: { value: getDate(tspe), error: false },
        tsa: parseNumber(tsa, 30) as 30 | 45 | 60 | 90 | 180,
        tsit: { value: getDate(tsit), error: false },
        tsaht: parseNumber(tsaht, 1),
        angleIsNotIgnore: lsit === 0,
    };

    const initState: IState = {
        tab: "operationMode",
        settingsShareVisible: false,
        savedAlertVisible: false,
        desktopMode: false,
        advancedMode: false,
        closeALert: false,
        isSettingsChanged: false,
        motionSensorIgnoreTimeError: false,
        lightSensorIgnoreTimeError: false,
        angleSensorIgnoreTimeError: false,
        sensorMode: getSensorModeState(deviceType, initSettings.motionSensorMode, initSettings.tsm),
        ...initSettings,
    };
    const [state, setState] = useState<IState>(initState);
    // imei устройства, настройками которого форма уже заполнена. Пока значение
    // совпадает с текущим устройством, обновления стора форму не трогают, чтобы
    // не затирать несохранённые правки пользователя
    const populatedImeiRef = useRef<string | null>(null);
    // разрешает однократное перезаполнение формы после сохранения из неё же
    const repopulateAfterSaveRef = useRef(false);

    const periodStartAndEndValidate = (
        mode: number | string,
        start: IDateInput,
        end: IDateInput
    ) => {
        if (mode !== 2) return true;
        const [a, b] = [start, end].map((item) => {
            const date = item.value.toDate();
            const hours = date.getHours();
            const minutes = date.getMinutes();
            return hours * 60 + minutes;
        });
        if (a !== b) {
            setState((prev) => ({
                ...prev,
                periodStartAndEndError: false,
            }));
            return true;
        } else {
            setState((prev) => ({
                ...prev,
                periodStartAndEndError: true,
            }));
            return false;
        }
    };
    const anglePeriodStartAndEndValidate = (
        mode: number | string,
        start: IDateInput,
        end: IDateInput
    ) => {
        if (mode !== 2) return true;
        const [a, b] = [start, end].map((item) => {
            const date = item.value.toDate();
            const hours = date.getHours();
            const minutes = date.getMinutes();
            return hours * 60 + minutes;
        });
        if (a !== b) {
            setState((prev) => ({
                ...prev,
                anglePeriodStartAndEndError: false,
            }));
            return true;
        } else {
            setState((prev) => ({
                ...prev,
                anglePeriodStartAndEndError: true,
            }));
            return false;
        }
    };
    const lightPeriodStartAndEndValidate = (
        mode: number | string,
        start: IDateInput,
        end: IDateInput
    ) => {
        if (mode !== 2) return true;
        const [a, b] = [start, end].map((item) => {
            const date = item.value.toDate();
            const hours = date.getHours();
            const minutes = date.getMinutes();
            return hours * 60 + minutes;
        });
        if (a !== b) {
            setState((prev) => ({
                ...prev,
                lightPeriodStartAndEndError: false,
            }));
            return true;
        } else {
            setState((prev) => ({
                ...prev,
                lightPeriodStartAndEndError: true,
            }));
            return false;
        }
    };
    const gnssMinSearchTimeValidate = (time: number) => {
        if (time < 60 || time > 180 || time % 10) {
            setState((prev) => ({
                ...prev,
                gnssMinSearchTimeError: true,
            }));
            return false;
        } else {
            setState((prev) => ({
                ...prev,
                gnssMinSearchTimeError: false,
            }));
            return true;
        }
    };
    const gnssMaxSearchTimeValidate = (time: number) => {
        if (time < 5 || time > 15) {
            setState((prev) => ({
                ...prev,
                gnssMaxSearchTimeError: true,
            }));
            return false;
        } else {
            setState((prev) => ({
                ...prev,
                gnssMaxSearchTimeError: false,
            }));
            return true;
        }
    };
    const coordTxPeriodValidate = (period: number | null) => {
        if (typeof period !== "number" || period < 0 || period > 10) {
            setState((prev) => ({
                ...prev,
                coordTxPeriodError: true,
            }));
            return false;
        } else {
            setState((prev) => ({
                ...prev,
                coordTxPeriodError: false,
            }));
            return true;
        }
    };
    const periodicSleepTimeValidate = (time: number | null) => {
        if (time === null || time < 1) {
            setState((prev) => ({
                ...prev,
                periodicSleepTimeError: true,
            }));
            return false;
        } else {
            setState((prev) => ({
                ...prev,
                periodicSleepTimeError: false,
            }));
            return true;
        }
    };
    const periodicActiveTimeValidate = (time: number | null) => {
        if (typeof time !== "number" || time < 3 || time > 15) {
            setState((prev) => ({
                ...prev,
                periodicActiveTimeError: true,
            }));
            return false;
        } else {
            setState((prev) => ({
                ...prev,
                periodicActiveTimeError: false,
            }));
            return true;
        }
    };
    const periodicSleepDaysValidate = (days: number | null) => {
        if (typeof days !== "number" || days < 0 || days > 30) {
            setState((prev) => ({
                ...prev,
                periodicSleepDaysError: true,
            }));
            return false;
        } else {
            setState((prev) => ({
                ...prev,
                periodicSleepDaysError: false,
            }));
            return true;
        }
    };
    const periodicSleepHoursValidate = (hours: number | null) => {
        if (typeof hours !== "number" || hours < 0 || hours > 23) {
            setState((prev) => ({
                ...prev,
                periodicSleepHoursError: true,
            }));
            return false;
        } else {
            setState((prev) => ({
                ...prev,
                periodicSleepHoursError: false,
            }));
            return true;
        }
    };
    const periodicSleepMinutesValidate = (
        minutes: number | null,
        hours: number | null
    ) => {
        if (
            typeof minutes !== "number" ||
            (minutes < 1 && hours == 0) ||
            minutes > 59
        ) {
            setState((prev) => ({
                ...prev,
                periodicSleepMinutesError: true,
            }));
            return false;
        } else {
            setState((prev) => ({
                ...prev,
                periodicSleepMinutesError: false,
            }));
            return true;
        }
    };
    const periodicLocationPeriodValidate = (time: number | null) => {
        if (typeof time !== "number" || time < 1) {
            setState((prev) => ({
                ...prev,
                periodicLocationPeriodError: true,
            }));
            return false;
        } else {
            setState((prev) => ({
                ...prev,
                periodicLocationPeriodError: false,
            }));
            return true;
        }
    };
    const motionSensorIgnoreTimeValidate = (
        ignoreTime: IDateInput,
        isNotIgnore: boolean,
        mode: number | string
    ): boolean => {
        const isIgnoreTimeValid =
            (!isNotIgnore &&
                !ignoreTime.error &&
                encodeDate(ignoreTime.value) <= 720) ||
            isNotIgnore ||
            mode === 0;
        setState((prev) => ({
            ...prev,
            motionSensorIgnoreTimeError: !isIgnoreTimeValid,
        }));
        return isIgnoreTimeValid;
    };
    const lightSensorIgnoreTimeValidate = (
        lightIgnoreTime: IDateInput,
        lightIsNotIgnore: boolean,
        mode: number | string
    ): boolean => {
        const isIgnoreTimeValid =
            (!lightIsNotIgnore &&
                !lightIgnoreTime.error &&
                encodeDate(lightIgnoreTime.value) <= 720) ||
            lightIsNotIgnore ||
            mode === 0;
        setState((prev) => ({
            ...prev,
            lightSensorIgnoreTimeError: !isIgnoreTimeValid,
        }));
        return isIgnoreTimeValid;
    };
    const angleSensorIgnoreTimeValidate = (
        angleIgnoreTime: IDateInput,
        angleIsNotIgnore: boolean,
        mode: number | string
    ): boolean => {
        const isIgnoreTimeValid =
            (!angleIsNotIgnore &&
                !angleIgnoreTime.error &&
                encodeDate(angleIgnoreTime.value) <= 720) ||
            angleIsNotIgnore ||
            mode === 0;
        setState((prev) => ({
            ...prev,
            angleSensorIgnoreTimeError: !isIgnoreTimeValid,
        }));
        return isIgnoreTimeValid;
    };
    const alarmStateValidate = (alarmState: IAlarmState<boolean>) => {
        let isAnyAlarmOn = false;
        for (let key in alarmState) {
            if (alarmState[key as keyof typeof alarmState]) isAnyAlarmOn = true;
        }
        setState((prev) => ({ ...prev, alarmError: !isAnyAlarmOn }));
        return isAnyAlarmOn;
    };
    const handleAlarmStateChange: MouseEventHandler<HTMLButtonElement> = (
        e
    ) => {
        const { id } = e.target as HTMLButtonElement;
        if (id) {
            setState((prev) => ({
                ...prev,
                alarmState: {
                    ...prev.alarmState,
                    [id]: !prev.alarmState[id as keyof typeof prev.alarmState],
                },
            }));
        }
        handleInputClick();
    };
    const wakeUpTimeValidate = (wakeUpTime: IAlarmState<IDateInput>) => {
        let error = false;
        for (let key in wakeUpTime) {
            if (wakeUpTime[key as keyof typeof wakeUpTime]?.error) error = true;
        }
        return !error;
    };
    const wakeUpActiveTimeValidate = (
        wakeUpActiveTime: IAlarmState<INumberInput>
    ) => {
        let globalError = false;
        for (let key in wakeUpActiveTime) {
            let error: boolean;
            const time =
                wakeUpActiveTime[key as keyof typeof wakeUpActiveTime]?.value;
            if (!time || time < 3 || time > 15) {
                globalError = true;
                error = true;
            } else error = false;
            setState((prev) => ({
                ...prev,
                wakeUpActiveTime: {
                    ...prev.wakeUpActiveTime,
                    [key]: {
                        ...prev.wakeUpActiveTime[
                            key as keyof typeof wakeUpActiveTime
                        ],
                        error,
                    },
                },
            }));
        }
        return !globalError;
    };
    const settingsValidate = () => {
        let isValid = false;
        let alarmState = {
            alarm1: false,
            alarm2: false,
            alarm3: false,
            alarm4: false,
            alarm5: false,
            alarm6: false,
        };
        const { mode } = state;
        if (mode === "tracker") {
            const { coordTxPeriod } = state;
            const isCoordTxPeriodValid = coordTxPeriodValidate(coordTxPeriod);
            isValid = isCoordTxPeriodValid;
        }
        if (mode === "periodic") {
            const {
                periodicActiveTime,
                periodicSleepDays,
                periodicSleepHours,
                periodicSleepMinutes,
                periodicLocationPeriod,
            } = state;
            const periodicSleepTime =
                periodicSleepDays !== null &&
                periodicSleepHours !== null &&
                periodicSleepMinutes !== null
                    ? (periodicSleepDays * 24 + periodicSleepHours) * 60 +
                      periodicSleepMinutes
                    : null;
            const isPeriodicSleepTimeValid =
                periodicSleepTimeValidate(periodicSleepTime);
            const isPeriodicActiveTimeValid =
                periodicActiveTimeValidate(periodicActiveTime);
            const isPeriodicSleepDaysValid =
                periodicSleepDaysValidate(periodicSleepDays);
            const isPeriodicSleepHoursValid =
                periodicSleepHoursValidate(periodicSleepHours);
            const isPeriodicSleepMinutesValid = periodicSleepMinutesValidate(
                periodicSleepMinutes,
                periodicSleepHours
            );
            const isPeriodicLocationPeriodValid =
                periodicLocationPeriodValidate(periodicLocationPeriod);
            isValid =
                isPeriodicActiveTimeValid &&
                isPeriodicSleepDaysValid &&
                isPeriodicSleepHoursValid &&
                isPeriodicSleepMinutesValid &&
                isPeriodicSleepTimeValid &&
                isPeriodicLocationPeriodValid;
        }
        if (mode === "alarm") {
            const { wakeUpTime, wakeUpActiveTime } = state;
            alarmState = state.alarmState as any;
            const isAlarmStateValid = alarmStateValidate(alarmState);
            const isWakeUpTimeValid = wakeUpTimeValidate(wakeUpTime);
            const isWakeUpActiveTimeValid =
                wakeUpActiveTimeValidate(wakeUpActiveTime);
            isValid =
                isAlarmStateValid &&
                isWakeUpTimeValid &&
                isWakeUpActiveTimeValid;
        }
        if (isValid) {
            const {
                isNotIgnore,
                ignoreTime,
                lightIsNotIgnore,
                lsm,
                lsps,
                lspe,
                lsit,
                gnssMinSearchTime,
                gnssMaxSearchTime,
                motionSensorMode,
                periodStart,
                periodEnd,

                sensorMode,

                tsps,
                tspe,
                tsm,
            } = state;
            const isIgnoreTimeValid = motionSensorIgnoreTimeValidate(
                ignoreTime,
                isNotIgnore,
                motionSensorMode
            );

            const isGnssMinSearchTimeValid =
                gnssMinSearchTimeValidate(gnssMinSearchTime);
            const isGnssMaxSearchTimeValid =
                gnssMaxSearchTimeValidate(gnssMaxSearchTime);
            let isPeriodStartAndEndValid = true;
            if (sensorMode === SensorMode.Motion) {
                isPeriodStartAndEndValid =
                    isPeriodStartAndEndValid &&
                    periodStartAndEndValidate(
                        motionSensorMode,
                        periodStart,
                        periodEnd
                    );
            } else if (sensorMode === SensorMode.Angle) {
                isPeriodStartAndEndValid =
                    isPeriodStartAndEndValid &&
                    anglePeriodStartAndEndValidate(tsm, tsps, tspe);
            }
            let isLightIgnoreTimeValid = true;
            let isLightPeriodValid = true;
            if (deviceType.startsWith("MX600")) {
                isLightIgnoreTimeValid = lightSensorIgnoreTimeValidate(
                    lsit,
                    lightIsNotIgnore,
                    lsm as number
                );
                isLightPeriodValid = lightPeriodStartAndEndValidate(
                    lsm,
                    lsps,
                    lspe
                );
            }
            isValid =
                isIgnoreTimeValid &&
                isGnssMinSearchTimeValid &&
                isGnssMaxSearchTimeValid &&
                isPeriodStartAndEndValid &&
                isLightIgnoreTimeValid;
        }
        return isValid;
    };
    const getSettings = () => {
        const {
            mode: currentMode,
            coordTxPeriod,
            wakeUpTime,
            wakeUpActiveTime,
            alarmState,
            alarmAction,
            periodicActiveTime,
            periodicSleepDays,
            periodicSleepHours,
            periodicSleepMinutes,
            periodicLocationPeriod,
            motionSensorMode,
            periodStart,
            periodEnd,
            accelerometerAmp,
            isNotIgnore,
            ignoreTime,
            preliminaryAlarmSend,
            smsState,
            smsFormat,
            phoneNumSim1,
            phoneNumSim2,
            serverState,
            serverProtocol,
            serverAddress,
            serverPort,
            serverPassword,
            mainSim,
            simApn,
            simLogin,
            simPassword,
            dnsAddress,
            timeSyncSatsState,
            timeSyncGsmState,
            timeSyncNtpState,
            ntpAddress,
            ntpPort,
            gnssPowersave,
            gnssMinSearchTime,
            gnssMaxSearchTime,
            gnssRectificationTime,
            ftpAddress,
            ftpPort,
            ftpLogin,
            ftpPassword,
            ftpDirectory,
            ftpFilename,
            ledState,
            deviceTimezone,
            sensorMode
        } = state;
        const result: IRawSettings = {
            mode: currentMode,
            motionSensorMode,
            ignoreTime: isNotIgnore ? 0 : encodeDate(ignoreTime.value),
            preliminaryAlarmSend,
            alarmState,
            coordTxPeriod: coordTxPeriod ?? initSettings.coordTxPeriod,
            periodicActiveTime:
                periodicActiveTime ?? initSettings.periodicActiveTime,
            periodicSleepTime:
                periodicSleepDays !== null &&
                periodicSleepHours !== null &&
                periodicSleepMinutes !== null
                    ? (periodicSleepDays * 24 + periodicSleepHours) * 60 +
                      periodicSleepMinutes
                    : null,
            periodicLocationPeriod,
            accelerometerAmp: accelerometerAmp ?? initSettings.accelerometerAmp,
            serverState,
            serverProtocol,
            serverAddress,
            serverPort,
            serverPassword,
            simApn,
            simLogin,
            simPassword,
            smsState,
            smsFormat,
            phoneNumSim1,
            phoneNumSim2,
            mainSim,
            periodStart: encodeDate(periodStart.value),
            periodEnd: encodeDate(periodEnd.value),
            wakeUpTime: {
                alarm1: encodeDate(wakeUpTime.alarm1?.value),
                alarm2: encodeDate(wakeUpTime.alarm2?.value),
                alarm3: encodeDate(wakeUpTime.alarm3?.value),
                alarm4: encodeDate(wakeUpTime.alarm4?.value),
                alarm5: encodeDate(wakeUpTime.alarm5?.value),
                alarm6: encodeDate(wakeUpTime.alarm6?.value),
            },
            wakeUpActiveTime: {
                alarm1: wakeUpActiveTime.alarm1?.value,
                alarm2: wakeUpActiveTime.alarm2?.value,
                alarm3: wakeUpActiveTime.alarm3?.value,
                alarm4: wakeUpActiveTime.alarm4?.value,
                alarm5: wakeUpActiveTime.alarm5?.value,
                alarm6: wakeUpActiveTime.alarm6?.value,
            },
            alarmAction,
            dnsAddress,
            timeSyncSatsState,
            timeSyncGsmState,
            timeSyncNtpState,
            ntpAddress,
            ntpPort,
            gnssPowersave,
            gnssMinSearchTime,
            gnssMaxSearchTime: gnssMaxSearchTime * 60,
            gnssRectificationTime,
            ftpAddress,
            ftpPort,
            ftpLogin,
            ftpPassword,
            ftpDirectory,
            ftpFilename,
            ledState,
            deviceTimezone,
        };
        const removeTracker = () => {
            delete result.coordTxPeriod;
        };
        const removePeriodic = () => {
            delete result.periodicActiveTime;
            delete result.periodicSleepTime;
            delete result.periodicLocationPeriod;
        };
        const removeAlarm = () => {
            delete result.alarmState;
            delete result.alarmAction;
            delete result.wakeUpTime;
            delete result.wakeUpActiveTime;
        };
        switch (currentMode) {
            case "tracker":
                removePeriodic();
                removeAlarm();
                break;
            case "periodic":
                removeTracker();
                removeAlarm();
                break;
            case "alarm":
                removeTracker();
                removePeriodic();
                break;
        }

        if (deviceType.startsWith("MX600")) {
            result.lsm = state.lsm;
            result.lssm = 0;//state.lssm; ВРЕМЕННО УБРАНО
            result.lsps = encodeDate(state.lsps.value);
            result.lspe = encodeDate(state.lspe.value);
            result.lsit = encodeDate(state.lsit.value);
        }
        if(deviceType.startsWith("MX500") || deviceType.startsWith("MX510") || deviceType.startsWith("MX600")){
            result.tsm = state.tsm;
            result.tsps =  encodeDate(state.tsps.value);
            result.tspe =  encodeDate(state.tspe.value);
            result.tsa = state.tsa;
            result.tsit =  encodeDate(state.tsit.value);
            result.tsaht = state.tsaht;

            if(sensorMode === SensorMode.Angle){
                result.motionSensorMode = 0;
            }else if(sensorMode === SensorMode.Motion){
                result.tsm = 0;
            }
        }

        if (!values.preliminaryAlarmSendSupported) {
            delete result.preliminaryAlarmSend;
        }
        if (!values.timeSyncSatsSupported) {
            delete result.timeSyncSatsState;
        }
        if (!values.timeSyncGsmSupported) {
            delete result.timeSyncGsmState;
        }
        if (!values.timeSyncNtpSupported) {
            delete result.timeSyncNtpState;
        }

        return result;
    };
    const getFilteredOptions = () => {
        const result = getSettings();
        const currentMode = result.mode;
        for (let k in result) {
            const isPassKey = currentMode === "alarm" && k === "alarmState";
            if (isPassKey) continue;
            const a = result[k as keyof typeof result];
            const b = settings[k as keyof typeof settings];
            if (k === "ftpFilename") delete result[k];
            else if (typeof a === "object" && typeof b === "object") {
                let isDiff = false;
                for (let kk in a) {
                    let aa = a[kk as keyof typeof a];
                    let bb = b[kk as keyof typeof b];
                    if (typeof aa === "number" || typeof bb === "number") {
                        if (`${aa}` !== `${bb}`) isDiff = true;
                    } else {
                        if (aa !== bb) isDiff = true;
                    }
                }
                if (!isDiff) delete result[k as keyof typeof result];
            }
            if (typeof a === "number" || typeof b === "number") {
                if (`${a}` === `${b}`) delete result[k as keyof typeof result];
            }
            if (typeof a === "boolean" || typeof b === "boolean") {
                if (b !== undefined) {
                    const aa = a === 1 || a === "1" || a === true;
                    const bb = b === 1 || b === "1" || b === true;
                    if (bb === aa) delete result[k as keyof typeof result];
                }
            } else {
                if (a === b) delete result[k as keyof typeof result];
            }
        }
        if (currentMode === "alarm" && currentMode === mode) {
            const a = result.alarmState;
            const b = settings.alarmState;
            if (typeof a === "object" && b) {
                let isDiff = false;
                for (let k in a) {
                    const aa = a[k as keyof typeof a];
                    const bb = b[k as keyof typeof b];
                    if (aa !== bb) isDiff = true;
                }
                if (!isDiff) {
                    delete result.alarmState;
                    delete result.mode;
                }
            }
        } else if (currentMode === mode) delete result.mode;
        return result;
    };
    const handleSettingsShareOpen = () => {
        const isValid = settingsValidate();
        if (isValid) {
            const result = getSettings();
            devices.setSharedSettings(result as any);
            setState((prev) => ({ ...prev, settingsShareVisible: true }));
        }
    };
    const handleSettingsShareClose = () => {
        app.resetDemoModeError();
        setState((prev) => ({ ...prev, settingsShareVisible: false }));
    };
    const handleLedStateChange = () => {
        setState((prev) => ({
            ...prev,
            ledState: !prev.ledState,
        }));
    };
    const handleNotIgnoreChange = () => {
        setState((prev) => ({
            ...prev,
            isNotIgnore: !prev.isNotIgnore,
        }));
    };
    const handlePreliminaryAlarmSendChange = () => {
        setState((prev) => ({
            ...prev,
            preliminaryAlarmSend: !prev.preliminaryAlarmSend,
        }));
    };
    const handleAngleNotIgnoreChange = () => {
        setState((prev) => ({
            ...prev,
            angleIsNotIgnore: !prev.angleIsNotIgnore,
        }));
    };
    const handleLightNotIgnoreChange = () => {
        setState((prev) => ({
            ...prev,
            lightIsNotIgnore: !prev.lightIsNotIgnore,
        }));
    };
    const handleSmsStateChange = () => {
        setState((prev) => ({
            ...prev,
            smsState: !prev.smsState,
        }));
    };
    const handleSmsFormatChange: SelectProps<number>["onChange"] = ({
        target,
    }) => {
        let smsFormat: number;
        const { value } = target;
        if (typeof value === "number") {
            smsFormat = value;
        } else if (typeof value === "string") {
            smsFormat = parseInt(value, 10);
        } else {
            return;
        }
        setState((prev) => ({ ...prev, smsFormat }));
    };
    const handleMainSimChange: SelectProps<number>["onChange"] = ({
        target,
    }) => {
        let mainSim: number;
        const { value } = target;
        if (typeof value === "number") {
            mainSim = value;
        } else if (typeof value === "string") {
            mainSim = parseInt(value, 10);
        } else {
            return;
        }
        setState((prev) => ({ ...prev, mainSim }));
    };
    const handlePhoneNumSim1Change: ChangeEventHandler<HTMLInputElement> = (
        e
    ) => {
        const filter = /[^0-9\+]/g;
        const reqExp = /(\+?(\d*)?)/;
        const val = e.target.value.replace(filter, "").match(reqExp);
        const phoneNumSim1 = val ? val[0] : "";
        setState((prev) => ({
            ...prev,
            phoneNumSim1,
        }));
    };
    const handlePhoneNumSim2Change: ChangeEventHandler<HTMLInputElement> = (
        e
    ) => {
        const phoneNumSim2 = e.target.value;
        setState((prev) => ({ ...prev, phoneNumSim2 }));
    };
    const handleGnssPowersaveChange = () => {
        setState((prev) => ({
            ...prev,
            gnssPowersave: !prev.gnssPowersave,
        }));
        handleInputClick();
    };
    const handleGnssMinSearchTimeChange: ChangeEventHandler<
        HTMLInputElement
    > = (e) => {
        const gnssMinSearchTime = parseInt(e.target.value, 10);
        setState((prev) => ({ ...prev, gnssMinSearchTime }));
    };
    const handleGnssMaxSearchTimeChange: ChangeEventHandler<
        HTMLInputElement
    > = (e) => {
        const gnssMaxSearchTime = parseInt(e.target.value, 10);
        setState((prev) => ({ ...prev, gnssMaxSearchTime }));
    };
    const handleGnssRectificationTimeChange: SelectProps<number>["onChange"] =
        ({ target }) => {
            let gnssRectificationTime: number;
            const { value } = target;
            if (typeof value === "number") {
                gnssRectificationTime = value;
            } else if (typeof value === "string") {
                gnssRectificationTime = parseInt(value, 10);
            } else {
                return;
            }
            setState((prev) => ({
                ...prev,
                gnssRectificationTime,
            }));
        };
    const handleTimeSyncSatsStateChange = () => {
        setState((prev) => ({
            ...prev,
            timeSyncSatsState: !prev.timeSyncSatsState,
        }));
        handleInputClick();
    };
    const handleTimeSyncGsmStateChange = () => {
        setState((prev) => ({
            ...prev,
            timeSyncGsmState: !prev.timeSyncGsmState,
        }));
        handleInputClick();
    };
    const handleTimeSyncNtpStateChange = () => {
        setState((prev) => ({
            ...prev,
            timeSyncNtpState: !prev.timeSyncNtpState,
        }));
        handleInputClick();
    };
    const handleNtpAddressChange: ChangeEventHandler<HTMLInputElement> = (
        e
    ) => {
        const val = e.target.value;
        setState((prev) => ({
            ...prev,
            ntpAddress: val,
        }));
    };
    const handleNtpPortChange: ChangeEventHandler<HTMLInputElement> = ({
        target,
    }) => {
        let ntpPort: number | null;
        const { value } = target;
        if (value === "" || value === undefined || value === null) {
            ntpPort = null;
        } else if (typeof value === "number") {
            ntpPort = value;
        } else if (typeof value === "string") {
            const regExp = /^(\d)*/;
            if (!regExp.test(value)) return;
            ntpPort = parseInt(value, 10);
            if (isNaN(ntpPort)) return;
        } else {
            return;
        }
        setState((prev) => ({
            ...prev,
            ntpPort,
        }));
    };
    const handleFtpAddressChange: ChangeEventHandler<HTMLInputElement> = (
        e
    ) => {
        const val = e.target.value;
        setState((prev) => ({
            ...prev,
            ftpAddress: val,
        }));
    };
    const handleFtpPortChange: ChangeEventHandler<HTMLInputElement> = ({
        target,
    }) => {
        let ftpPort: number | null;
        const { value } = target;
        if (value === "" || value === undefined || value === null) {
            ftpPort = null;
        } else if (typeof value === "number") {
            ftpPort = value;
        } else if (typeof value === "string") {
            const regExp = /^(\d)*/;
            if (!regExp.test(value)) return;
            ftpPort = parseInt(value, 10);
            if (isNaN(ftpPort)) return;
        } else {
            return;
        }
        setState((prev) => ({
            ...prev,
            ftpPort,
        }));
    };
    const handleFtpLoginChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        const ftpLogin = e.target.value;
        setState((prev) => ({
            ...prev,
            ftpLogin,
        }));
    };
    const handleFtpPasswordChange: ChangeEventHandler<HTMLInputElement> = (
        e
    ) => {
        const ftpPassword = e.target.value;
        setState((prev) => ({
            ...prev,
            ftpPassword,
        }));
    };
    const handleFtpDirectoryChange: ChangeEventHandler<HTMLInputElement> = (
        e
    ) => {
        const ftpDirectory = e.target.value;
        setState((prev) => ({
            ...prev,
            ftpDirectory,
        }));
    };
    const handleFtpFilenameChange: ChangeEventHandler<HTMLInputElement> = (
        e
    ) => {
        const ftpFilename = e.target.value;
        setState((prev) => ({
            ...prev,
            ftpFilename,
        }));
    };
    const handleServerStateChange: MouseEventHandler<HTMLElement> = ({
        target,
    }) => {
        const { id } = target as HTMLElement;
        if (id) {
            setState((prev) => ({
                ...prev,
                serverState: {
                    ...prev.serverState,
                    [id]: !prev.serverState[
                        id as keyof typeof prev.serverState
                    ],
                },
            }));
        }
        handleInputClick();
    };
    const handleServerProtocolChange: BindedHandler<SelectChangeEvent> = (
        name,
        e
    ) => {
        if (name) {
            const protocol = e.target.value;
            setState((prev) => ({
                ...prev,
                serverProtocol: {
                    ...prev.serverProtocol,
                    [name]: protocol,
                },
            }));
        }
    };
    const handleServerAddressChange: BindedHandler<
        ChangeEvent<HTMLInputElement>
    > = (name, e) => {
        if (name) {
            const val = e.target.value;
            setState((prev) => ({
                ...prev,
                serverAddress: {
                    ...prev.serverAddress,
                    [name]: val,
                },
            }));
        }
    };
    const handleServerPortChange: BindedHandler<
        ChangeEvent<HTMLInputElement>
    > = (name, { target }) => {
        if (name) {
            let port: number | null;
            const { value } = target;
            if (value === "" || value === undefined || value === null) {
                port = null;
            } else if (typeof value === "number") {
                port = value;
            } else if (typeof value === "string") {
                const regExp = /^(\d)*/;
                if (!regExp.test(value)) return;
                port = parseInt(value, 10);
                if (isNaN(port)) return;
            } else {
                return;
            }
            setState((prev) => ({
                ...prev,
                serverPort: {
                    ...prev.serverPort,
                    [name]: port,
                },
            }));
        }
    };
    const handleServerPasswordChange: BindedHandler<
        ChangeEvent<HTMLInputElement>
    > = (name, e) => {
        if (name) {
            const password = e.target.value;
            setState((prev) => ({
                ...prev,
                serverPassword: {
                    ...prev.serverPassword,
                    [name]: password,
                },
            }));
        }
    };
    const handleDnsAddressChange: BindedHandler<
        ChangeEvent<HTMLInputElement>
    > = (name, e) => {
        if (name) {
            const val = e.target.value;
            setState((prev) => ({
                ...prev,
                dnsAddress: {
                    ...prev.dnsAddress,
                    [name]: val,
                },
            }));
        }
    };
    const handleSimApnChange: BindedHandler<ChangeEvent<HTMLInputElement>> = (
        name,
        e
    ) => {
        if (name) {
            const apn = e.target.value;
            setState((prev) => ({
                ...prev,
                simApn: {
                    ...prev.simApn,
                    [name]: apn,
                },
            }));
        }
    };
    const handleSimLoginChange: BindedHandler<ChangeEvent<HTMLInputElement>> = (
        name,
        e
    ) => {
        if (name) {
            const login = e.target.value;
            setState((prev) => ({
                ...prev,
                simLogin: {
                    ...prev.simLogin,
                    [name]: login,
                },
            }));
        }
    };
    const handleSimPasswordChange: BindedHandler<
        ChangeEvent<HTMLInputElement>
    > = (name, e) => {
        if (name) {
            const password = e.target.value;
            setState((prev) => ({
                ...prev,
                simPassword: {
                    ...prev.simPassword,
                    [name]: password,
                },
            }));
        }
    };
    const handleWakeUpChange: BindedHandler<Moment> = (name, date) => {
        if (name) {
            setState((prev) => ({
                ...prev,
                wakeUpTime: {
                    ...prev.wakeUpTime,
                    [name]: {
                        value: date,
                        error: prev.wakeUpTime[
                            name as keyof typeof prev.wakeUpTime
                        ]?.error,
                    },
                },
            }));
        }
    };
    const handleWakeUpError: BindedHandler<unknown> = (name, reason) => {
        if (name) {
            setState((prev) => ({
                ...prev,
                wakeUpTime: {
                    ...prev.wakeUpTime,
                    [name]: {
                        value: prev.wakeUpTime[
                            name as keyof typeof prev.wakeUpActiveTime
                        ]?.value,
                        error: !!reason,
                    },
                },
            }));
        }
    };
    const handlePeriodStartChange: MomentChangeHandler = (value) => {
        setState((prev) => ({
            ...prev,
            periodStart: {
                value,
                error: prev.periodStart.error,
            },
        }));
    };

    const handleAnglePeriodStartChange: MomentChangeHandler = (value) => {
        setState((prev) => ({
            ...prev,
            tsps: {
                value,
                error: prev.tsps.error,
            },
        }));
    };

    const handlePeriodEndChange: MomentChangeHandler = (value) => {
        setState((prev) => ({
            ...prev,
            periodEnd: {
                value,
                error: prev.periodEnd.error,
            },
        }));
    };
    const handleAnglePeriodEndChange: MomentChangeHandler = (value) => {
        setState((prev) => ({
            ...prev,
            tspe: {
                value,
                error: prev.tspe.error,
            },
        }));
    };
    const handlePeriodStartError: MomentErrorHandler = (reason) => {
        setState((prev) => ({
            ...prev,
            periodStart: {
                value: prev.periodStart.value,
                error: !!reason,
            },
        }));
    };
    const handleAnglePeriodStartError: MomentErrorHandler = (reason) => {
        setState((prev) => ({
            ...prev,
            tsps: {
                value: prev.tsps.value,
                error: !!reason,
            },
        }));
    };
    const handlePeriodEndError: MomentErrorHandler = (reason) => {
        setState((prev) => ({
            ...prev,
            periodEnd: {
                value: prev.periodEnd.value,
                error: !!reason,
            },
        }));
    };
    const handleAnglePeriodEndError: MomentErrorHandler = (reason) => {
        setState((prev) => ({
            ...prev,
            tspe: {
                value: prev.tspe.value,
                error: !!reason,
            },
        }));
    };

    const handleLightPeriodStartChange: MomentChangeHandler = (value) => {
        setState((prev) => ({
            ...prev,
            lsps: {
                value,
                error: prev.lsps.error,
            },
        }));
    };
    const handleLightPeriodEndChange: MomentChangeHandler = (value) => {
        setState((prev) => ({
            ...prev,
            lspe: {
                value,
                error: prev.lspe.error,
            },
        }));
    };
    const handleLightPeriodStartError: MomentErrorHandler = (reason) => {
        setState((prev) => ({
            ...prev,
            lsps: {
                value: prev.lsps.value,
                error: !!reason,
            },
        }));
    };
    const handleLightPeriodEndError: MomentErrorHandler = (reason) => {
        setState((prev) => ({
            ...prev,
            lspe: {
                value: prev.periodEnd.value,
                error: !!reason,
            },
        }));
    };

    const handleWakeUpActiveTimeChange: BindedHandler<
        ChangeEvent<HTMLInputElement>
    > = (name, e) => {
        if (name) {
            const time = parseInt(e.target.value, 10);
            setState((prev) => ({
                ...prev,
                wakeUpActiveTime: {
                    ...prev.wakeUpActiveTime,
                    [name]: {
                        value: time,
                        error: prev.wakeUpActiveTime[
                            name as keyof typeof prev.wakeUpActiveTime
                        ]?.error,
                    },
                },
            }));
        }
    };
    const handleIgnoreTimeChange: MomentChangeHandler = (value) => {
        setState((prev) => ({
            ...prev,
            ignoreTime: {
                value,
                error: prev.ignoreTime.error,
            },
        }));
    };
    const handleAngleIgnoreTimeChange: MomentChangeHandler = (value) => {
        setState((prev) => ({
            ...prev,
            tsit: {
                value,
                error: prev.tsit.error,
            },
        }));
    };
    const handleIgnoreTimeError: MomentErrorHandler = (reason) => {
        setState((prev) => ({
            ...prev,
            ignoreTime: {
                value: prev.ignoreTime.value,
                error: !!reason,
            },
        }));
    };
    const handleAngleIgnoreTimeError: MomentErrorHandler = (reason) => {
        setState((prev) => ({
            ...prev,
            tsit: {
                value: prev.tsit.value,
                error: !!reason,
            },
        }));
    };

    const handleLightIgnoreTimeChange: MomentChangeHandler = (value) => {
        setState((prev) => ({
            ...prev,
            lsit: {
                value,
                error: prev.lsit.error,
            },
        }));
    };
    const handleLightIgnoreTimeError: MomentErrorHandler = (reason) => {
        setState((prev) => ({
            ...prev,
            lsit: {
                value: prev.lsit.value,
                error: !!reason,
            },
        }));
    };

    const handleAlarmActionChange: BindedHandler<SelectChangeEvent<number>> = (
        name,
        e
    ) => {
        if (name) {
            const action = e.target.value;
            setState((prev) => ({
                ...prev,
                alarmAction: {
                    ...prev.alarmAction,
                    [name]: action,
                },
            }));
        }
    };
    const handleAccelerometerAmpChange: SelectProps<number>["onChange"] = ({
        target,
    }) => {
        let accelerometerAmp: number | null;
        const { value } = target;
        if (value === "" || value === undefined || value === null) {
            accelerometerAmp = null;
        } else if (typeof value === "number") {
            accelerometerAmp = value;
        } else if (typeof value === "string") {
            accelerometerAmp = parseInt(value, 10);
            if (isNaN(accelerometerAmp)) return;
        } else {
            return;
        }
        setState((prev) => ({ ...prev, accelerometerAmp }));
    };

    const handleAngleAmpChange: SelectProps<number>["onChange"] = ({
        target,
    }) => {
        let accelerometerAmp: number | null;
        const { value } = target;
        if (value === "" || value === undefined || value === null) {
            accelerometerAmp = null;
        } else if (typeof value === "number") {
            accelerometerAmp = value;
        } else if (typeof value === "string") {
            accelerometerAmp = parseInt(value, 10);
            if (isNaN(accelerometerAmp)) return;
        } else {
            return;
        }
        setState((prev) => ({
            ...prev,
            tsa: accelerometerAmp as 30 | 45 | 60 | 90 | 180,
        }));
    };
    const handleModeChange: SelectProps<mode>["onChange"] = (e) => {
        const mode = e.target.value;
        if (mode in modes) {
            setState((prev) => ({ ...prev, mode: mode as mode }));
        }
    };
    const handleCoordTxPeriodChange: ChangeEventHandler<HTMLInputElement> = ({
        target,
    }) => {
        let coordTxPeriod: number | null;
        const { value } = target;
        if (value === "" || value === undefined || value === null) {
            coordTxPeriod = null;
        } else if (typeof value === "number") {
            coordTxPeriod = value;
        } else if (typeof value === "string") {
            coordTxPeriod = parseInt(value, 10);
            if (isNaN(coordTxPeriod)) return;
        } else {
            return;
        }
        setState((prev) => ({ ...prev, coordTxPeriod }));
    };
    const handlePeriodicActiveTimeChange: ChangeEventHandler<
        HTMLInputElement
    > = ({ target }) => {
        let periodicActiveTime: number | null;
        const { value } = target;
        if (value === "" || value === undefined || value === null) {
            periodicActiveTime = null;
        } else if (typeof value === "number") {
            periodicActiveTime = value;
        } else if (typeof value === "string") {
            periodicActiveTime = parseInt(value, 10);
            if (isNaN(periodicActiveTime)) return;
        } else {
            return;
        }
        setState((prev) => ({ ...prev, periodicActiveTime }));
    };
    const handlePeriodicSleepDaysChange: ChangeEventHandler<
        HTMLInputElement
    > = ({ target }) => {
        let periodicSleepDays: number | null;
        const { value } = target;
        if (value === "" || value === undefined || value === null) {
            periodicSleepDays = null;
        } else if (typeof value === "number") {
            periodicSleepDays = value;
        } else if (typeof value === "string") {
            periodicSleepDays = parseInt(value, 10);
            if (isNaN(periodicSleepDays)) return;
        } else {
            return;
        }
        setState((prev) => ({ ...prev, periodicSleepDays }));
    };
    const handlePeriodicSleepHoursChange: ChangeEventHandler<
        HTMLInputElement
    > = ({ target }) => {
        let periodicSleepHours: number | null;
        const { value } = target;
        if (value === "" || value === undefined || value === null) {
            periodicSleepHours = null;
        } else if (typeof value === "number") {
            periodicSleepHours = value;
        } else if (typeof value === "string") {
            periodicSleepHours = parseInt(value, 10);
            if (isNaN(periodicSleepHours)) return;
        } else {
            return;
        }
        setState((prev) => ({ ...prev, periodicSleepHours }));
    };
    const handlePeriodicSleepMinutesChange: ChangeEventHandler<
        HTMLInputElement
    > = ({ target }) => {
        let periodicSleepMinutes: number | null;
        const { value } = target;
        if (value === "" || value === undefined || value === null) {
            periodicSleepMinutes = null;
        } else if (typeof value === "number") {
            periodicSleepMinutes = value;
        } else if (typeof value === "string") {
            periodicSleepMinutes = parseInt(value, 10);
            if (isNaN(periodicSleepMinutes)) return;
        } else {
            return;
        }
        setState((prev) => ({ ...prev, periodicSleepMinutes }));
    };
    const handlePeriodicLocationPeriodChange: ChangeEventHandler<
        HTMLInputElement
    > = ({ target }) => {
        let periodicLocationPeriod: number | null;
        const { value } = target;
        if (value === "" || value === undefined || value === null) {
            periodicLocationPeriod = null;
        } else if (typeof value === "number") {
            periodicLocationPeriod = value;
        } else if (typeof value === "string") {
            periodicLocationPeriod = parseInt(value, 10);
            if (isNaN(periodicLocationPeriod)) return;
        } else {
            return;
        }
        setState((prev) => ({ ...prev, periodicLocationPeriod }));
    };
    const handleMotionSensorModeChange: SelectProps["onChange"] = (e) => {
        const motionSensorMode = e.target.value as number;
        setState((prev) => ({ ...prev, motionSensorMode }));
    };

    const handleAngleSensorModeChange: SelectProps["onChange"] = (e) => {
        const angleSensorMode = e.target.value as 0 | 1 | 2;
        setState((prev) => ({ ...prev, tsm: angleSensorMode }));
    };

    const handleAngleHoldingTimeChange: SelectProps["onChange"] = (e) => {
        const tsaht = e.target.value as number;
        setState((prev) => ({ ...prev, tsaht: tsaht }));
    };

    const handleLightSensorModeChange: SelectProps["onChange"] = (e) => {
        const lightSensorMode = e.target.value as number;
        setState((prev) => ({ ...prev, lsm: lightSensorMode as 0 | 1 | 2 }));
    };

    const handleLightSensorTriggerModeChange: SelectProps["onChange"] = (e) => {
        const lightSensorTriggerMode = e.target.value as 0 | 1;
        setState((prev) => ({ ...prev, lssm: lightSensorTriggerMode }));
    };

    const handleAdvancedModeClick = () => {
        setState((prev) => ({ ...prev, advancedMode: !prev.advancedMode }));
    };
    const handleSaveClick = () => {
        app.resetDemoModeError();
        if (!app.demoMode) {
            const isValid = settingsValidate();
            if (isValid) {
                // const filtered = getFilteredOptions();
                const result = getSettings();
                repopulateAfterSaveRef.current = true;
                devices.sendDeviceSettings(result as any);
                // if (Object.keys(filtered).length !== 0) {
                //     devices.sendDeviceSettings(result);
                // }
            }
        } else app.setDemoModeError();
    };
    const handleInputClick = () => {
        app.resetDemoModeError();
        setState((prev) => {
            const wakeUpActiveTime = {
                ...prev.wakeUpActiveTime,
            };
            for (let key in wakeUpActiveTime) {
                if (
                    wakeUpActiveTime[key as keyof typeof wakeUpActiveTime] !==
                    undefined
                ) {
                    wakeUpActiveTime[
                        key as keyof typeof wakeUpActiveTime
                    ]!.error = false;
                }
            }
            return {
                ...prev,
                wakeUpActiveTime,
                coordTxPeriodError: false,
                periodicSleepTimeError: false,
                periodicActiveTimeError: false,
                periodicSleepDaysError: false,
                periodicSleepHoursError: false,
                periodicSleepMinutesError: false,
                periodicLocationPeriodError: false,
                alarmError: false,
                gnssMinSearchTimeError: false,
                gnssMaxSearchTimeError: false,
                periodStartAndEndError: false,
                motionSensorIgnoreTimeError: false,
            };
        });
    };
    const handleTabChange: TabsProps["onChange"] = (e, tab) => {
        setState((prev) => ({ ...prev, tab }));
    };
    const handleSettingsResetClick = () => {
        setState((prev) => ({ ...prev, ...(defSettings as any) }));
    };
    const handleCloseALertOpen = () => {
        app.resetDemoModeError();
        setState((prev) => ({ ...prev, closeALert: true }));
    };
    const handleCloseALertClose = () => {
        app.resetDemoModeError();
        setState((prev) => ({ ...prev, closeALert: false }));
    };
    const handleCloseClick = () => {
        const settingsStatus = devices.settingsList.find(
            (val) => val.imei === devices.settingDeviceImei
        );

        const isSettings =
            settingsStatus &&
            settingsStatus.status &&
            !settingsStatus.status.error;

        if (!isSettings) {
            onClose();
            return;
        }

        const settings = getFilteredOptions();
        const isEmpty = Object.keys(settings).length === 0;
        if (isEmpty) onClose();
        else handleCloseALertOpen();
    };
    const handleDeviceTimezoneChange: SelectProps["onChange"] = (e) => {
        const deviceTimezone = e.target.value as number;
        setState((prev) => ({ ...prev, deviceTimezone }));
    };
    const handleSaveAlertClose = () => {
        setState((prev) => ({ ...prev, savedAlertVisible: false }));
    };

    const handleSensorModeChange: SelectProps["onChange"] = (e) => {
        const sensorMode = e.target.value as 0 | 1;
        setState((prev) => ({ ...prev, sensorMode: sensorMode }));
    };

    const handlers = {
        handleModeChange,
        handleAlarmStateChange,
        handleWakeUpChange,
        handleWakeUpError,
        handleAlarmActionChange,
        handleWakeUpActiveTimeChange,
        handleCoordTxPeriodChange,
        handlePeriodicActiveTimeChange,
        handlePeriodicSleepDaysChange,
        handlePeriodicSleepHoursChange,
        handlePeriodicSleepMinutesChange,
        handlePeriodicLocationPeriodChange,
        handleMotionSensorModeChange,
        handleSaveClick,
        handleInputClick,
        handleTabChange,
        handlePeriodStartChange,
        handlePeriodEndChange,
        handlePeriodStartError,
        handlePeriodEndError,
        handleAccelerometerAmpChange,
        handleNotIgnoreChange,
        handlePreliminaryAlarmSendChange,
        handleIgnoreTimeChange,
        handleIgnoreTimeError,
        handleSmsStateChange,
        handleSmsFormatChange,
        handlePhoneNumSim1Change,
        handlePhoneNumSim2Change,
        handleServerStateChange,
        handleServerProtocolChange,
        handleServerAddressChange,
        handleServerPortChange,
        handleServerPasswordChange,
        handleMainSimChange,
        handleSimApnChange,
        handleSimLoginChange,
        handleSimPasswordChange,
        handleDnsAddressChange,
        handleTimeSyncSatsStateChange,
        handleTimeSyncGsmStateChange,
        handleTimeSyncNtpStateChange,
        handleNtpAddressChange,
        handleNtpPortChange,
        handleGnssPowersaveChange,
        handleGnssMinSearchTimeChange,
        handleGnssMaxSearchTimeChange,
        handleGnssRectificationTimeChange,
        handleFtpAddressChange,
        handleFtpPortChange,
        handleFtpLoginChange,
        handleFtpPasswordChange,
        handleFtpDirectoryChange,
        handleFtpFilenameChange,
        handleLedStateChange,
        handleDeviceTimezoneChange,
        handleSettingsShareOpen,
        handleSettingsShareClose,
        handleAdvancedModeClick,
        handleSettingsResetClick,
        handleCloseALertOpen,
        handleCloseALertClose,
        handleCloseClick,
        handleSaveAlertClose,
        handleLightSensorModeChange,
        handleLightPeriodStartChange,
        handleLightPeriodStartError,
        handleLightPeriodEndChange,
        handleLightPeriodEndError,
        handleLightNotIgnoreChange,
        handleLightIgnoreTimeChange,
        handleLightIgnoreTimeError,
        handleLightSensorTriggerModeChange,
        handleSensorModeChange,

        handleAngleSensorModeChange,
        handleAnglePeriodStartChange,
        handleAnglePeriodEndChange,
        handleAnglePeriodStartError,
        handleAnglePeriodEndError,
        handleAngleAmpChange,
        handleAngleNotIgnoreChange,
        handleAngleIgnoreTimeChange,
        handleAngleIgnoreTimeError,
        handleAngleHoldingTimeChange
    };

    useEffect(() => {
        const element = document.activeElement as HTMLElement;
        if (element) {
            element.blur();
        }
        app.resetDemoModeError();
        devices.settingDeviceImei &&
            devices.getDeviceSettings(devices.settingDeviceImei);
        return () => {
            app.resetDeviceSettingsClosing();
            app.closeDeviceSettings();
        };
    }, []);
    useEffect(() => {
        if (devices.operationSuccess) {
            devices.resetOperationSuccess();
            devices.settingDeviceImei &&
                devices.getDeviceSettings(devices.settingDeviceImei);
            setState((prev) => ({ ...prev, savedAlertVisible: true }));
        }
    }, [devices.operationSuccess]);
    useEffect(() => {
        const imei = devices.settingDeviceImei;
        const isPopulated = populatedImeiRef.current === imei;
        if (isPopulated && !repopulateAfterSaveRef.current) return;

        repopulateAfterSaveRef.current = false;
        // Пока запрос настроек в полёте, в сторе могут лежать настройки
        // предыдущего устройства или кэш прошлого открытия: форму ими заполняем
        // (она всё равно скрыта лоадером), но заполненной ещё не считаем.
        // Пустой ответ (нет прав, ошибка сервера) тоже не считаем заполнением
        if (!devices.settingsLoading && Object.keys(settings).length > 0) {
            populatedImeiRef.current = imei;
        }

        setState((prev) => ({
            ...prev,
            ...initSettings,
            sensorMode: getSensorModeState(
                deviceType,
                initSettings.motionSensorMode,
                initSettings.tsm
            ),
        }));
    }, [devices.selectedDeviceSettings]);
    useEffect(() => {
        setState((prev) => {
            const desktopMode = windowSize.width >= xsBreakpoint;
            return {
                ...prev,
                desktopMode,
            };
        });
    }, [windowSize.width]);
    useEffect(() => {
        const baseSettings = [
            "operationMode",
            "motionSensor",
            "dataTransmission",
            "timezone",
        ];
        const { advancedMode, tab } = state;
        if (!advancedMode && baseSettings.indexOf(tab) === -1) {
            setState((prev) => ({ ...prev, tab: "operationMode" }));
        }
    }, [state.advancedMode]);
    useEffect(() => {
        if (app.isDeviceSettingsClosing) {
            const settings = getFilteredOptions();
            const isEmpty = Object.keys(settings).length === 0;
            if (isEmpty) {
                app.resetDeviceSettingsClosing();
                app.setDeviceSettingsClosed();
            } else handleCloseALertOpen();
        }
    }, [app.isDeviceSettingsClosing]);
    useEffect(() => {
        if (state.isNotIgnore) {
            setState((prev) => ({
                ...prev,
                ignoreTime: { value: defDate, error: false },
            }));
        }
    }, [state.isNotIgnore]);
    useEffect(() => {
        if (state.lightIsNotIgnore) {
            setState((prev) => ({
                ...prev,
                lsit: { value: defDate, error: false },
            }));
        }
    }, [state.lightIsNotIgnore]);
    useEffect(() => {
        if (state.angleIsNotIgnore) {
            setState((prev) => ({
                ...prev,
                tsit: { value: defDate, error: false },
            }));
        }
    }, [state.angleIsNotIgnore]);
    useEffect(() => {
        if (state.motionSensorMode === 0 && state.ignoreTime.error) {
            setState((prev) => ({
                ...prev,
                ignoreTime: initSettings.ignoreTime,
                isNotIgnore: initSettings.isNotIgnore,
            }));
        }
    }, [state.motionSensorMode]);
    useEffect(() => {
        if (state.lsm === 0 && state.lsit.error) {
            setState((prev) => ({
                ...prev,
                lsit: initSettings.lsit,
                lightIsNotIgnore: initSettings.lightIsNotIgnore,
            }));
        }
    }, [state.lsm]);
    useEffect(() => {
        if (state.tsm === 0 && state.tsit.error) {
            setState((prev) => ({
                ...prev,
                tsit: initSettings.tsit,
                lightIsNotIgnore: initSettings.angleIsNotIgnore,
            }));
        }
    }, [state.tsm]);

    return {
        devices,
        user,
        app,
        lang,
        state,
        handlers,
        values,
    };
};

export default useDeviceSettings;
